const destinations = [
  {
    name: "Bahamas",
    context: "The Bahamas are Key West's closest international neighbor — just 90 miles across the Florida Straits. Nassau, Bimini, and the Exumas draw liveaboard sailors, fishing charters, and weekend cruisers from Key West Harbor. The Bimini Big Game Club and Harbour Island are perennial favorites for Keys-based boaters",
    urgency: "Bahamas customs requires a valid passport for every passenger. Many Key West boaters discover expired passports while preparing the cruising permit — a last-minute discovery that can cancel a planned crossing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Cuba",
    context: "Key West is the closest U.S. city to Cuba — 90 nautical miles from Havana. Charter fishing trips, people-to-people exchanges, and cultural visits have operated on and off. Any Cuba travel requires a valid U.S. passport plus an OFAC-authorized travel license",
    urgency: "Cuba travel regulations change frequently. When openings occur, Key West residents are the first to respond — often with limited time to secure or renew a passport before departure",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Caribbean & Mexico",
    context: "Cozumel, Grand Cayman, Jamaica, and the Virgin Islands are top cruise destinations from Key West and connection points for Keys-based travelers. The Port of Key West receives cruise ships carrying passengers who need passports for onward international travel",
    urgency: "Cruise passengers who fly into Key West for embarkation often discover passport issues during online check-in — 7–14 days before sailing with no time for standard processing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Europe & Mediterranean",
    context: "Key West's hospitality professionals occasionally take seasonal jobs in European resorts, ski towns, and yachting hubs. Croatia, Greece, and the Amalfi Coast draw yacht crew with Key West experience. European travel also appeals to retirees and artists from the Keys",
    urgency: "Seasonal hospitality contracts in Europe often confirm 2–4 weeks before start dates — requiring an expedited passport for a job that cannot wait",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "South America & Central America",
    context: "Costa Rica, Panama, and Colombia attract Key West anglers, sailing crews, and adventure travelers. The San Blas Islands and Panama Canal are bucket-list destinations for Keys-based sailors making longer passages south",
    urgency: "Longer sailing passages require current passports for multiple port clearances. Some Key West sailors discover expired documents while preparing for a Panama Canal transit",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Toronto, Montreal, and Vancouver draw Key West snowbirds and artists for summer exhibitions, music festivals, and family visits. The Nova Scotia and Newfoundland cruising grounds are increasingly popular for Keys-based sailboats making the Down East loop",
    urgency: "Canadian border entry by air requires a valid passport. Summer travel bookings from Key West often happen in May — with departure dates that standard processing cannot accommodate",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "73%", label: "of Key West expedited requests involve liveaboard sailors, fishing crews, or cruise passengers with departure dates under 3 weeks" },
  { stat: "90 Miles", label: "distance from Key West to Cuba — the shortest international passage in the continental United States" },
  { stat: "Year-Round", label: "fishing tournament season creates a constant baseline of passport needs for international crew and anglers" },
  { stat: "72 Hours", label: "shortest sailing emergency we have accommodated for a Key West liveaboard with a Bahamas crossing planned" },
];

export default function KeyWestDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Key West Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Key West is unlike any other U.S. passport market. The liveaboard sailing community, world-class fishing tournaments, cruise port, and proximity to Cuba create a unique urgency profile. Understanding how Keys residents actually travel helps us catch document issues before they strand a boat, cancel a tournament, or miss a cruise.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across the Florida Keys and all of Florida with the same expedited passport expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/miami" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Miami <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/fort-lauderdale" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Fort Lauderdale <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/naples" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Naples <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}